/**
 * BRAND ITEM CARD
 * 
 * Displays a single item on a brand page
 * Works with both official brand items and aggregated community items
 * Includes "Add to Collection" and "Add to Wishlist" buttons
 */

import { Link } from 'react-router-dom';
import { AppIcons } from '../../constants/icons';
import { AppStyles } from '../../constants/categoryColors';
import type { BrandItem, AggregatedItem } from '../../types/brand';
import {
  FINISH_OPTIONS,
  SPECIAL_FEATURE_OPTIONS,
} from '../../types';
import { slugify } from '../../utils/polishSlug';
import { itemTypeToPath } from '../../utils/itemTypeRoutes';
import { canonicalizeTags, titleCaseTag } from '../../utils/tagDisplay';
import TypeBadge from '../community/TypeBadge';
import { COMMUNITY_VERIFIED_MIN } from '../../services/polishConfirmService';

interface BrandItemCardProps {
  item: BrandItem | AggregatedItem;
  brandName: string;
  brandSlug: string;
  isCommunityItem?: boolean;  // True if from aggregated user data
  confirmCount?: number;      // community "Confirm" count for this polish
  onAddToCollection: () => void;
  onAddToWishlist: () => void;
}

export default function BrandItemCard({
  item,
  brandName: _brandName,
  brandSlug,
  isCommunityItem = false,
  confirmCount,
  onAddToCollection,
  onAddToWishlist
}: BrandItemCardProps) {
  // Route to the type-specific detail page. Older items lack itemType so
  // they default to 'polish' via itemTypeToPath — matches the historical
  // polish-only behavior. Uses stored nameSlug when present (Phase 1
  // added universal slug stamping); falls back to slugify(name) for
  // legacy items that predate that write.
  const rawItem = item as AggregatedItem & { nameSlug?: string };
  const detailPath = itemTypeToPath(rawItem.itemType);
  const nameSlugForUrl = rawItem.nameSlug || slugify(item.name);
  const polishHref = `/brands/${brandSlug}/${detailPath}/${nameSlugForUrl}`;
  // Determine if this is a BrandItem or AggregatedItem
  const isBrandItem = 'status' in item;
  
  // Get photo URL - handle multiple formats:
  // 1. photos: [{url: '...', creditName: '...'}] - object array with credit
  // 2. photos: ['...'] - string array  
  // 3. representativePhoto: {url: '...'} - legacy format
  const getPhotoUrl = (): string | undefined => {
    // Check photos array first (works for both BrandItem and AggregatedItem)
    if (item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      const firstPhoto = item.photos[0];
      // If it's a string URL
      if (typeof firstPhoto === 'string') {
        return firstPhoto;
      }
      // If it's an object with url property
      if (typeof firstPhoto === 'object' && firstPhoto && 'url' in firstPhoto) {
        return (firstPhoto as { url: string }).url;
      }
    }
    // Check for representativePhoto (legacy AggregatedItem format)
    if ('representativePhoto' in item && item.representativePhoto?.url) {
      return item.representativePhoto.url;
    }
    return undefined;
  };

  // Get photo credit from the first photo if available
  const getPhotoCredit = (): { name?: string; url?: string } | undefined => {
    if (item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      const firstPhoto = item.photos[0];
      if (typeof firstPhoto === 'object' && firstPhoto && 'creditName' in firstPhoto) {
        const photo = firstPhoto as { creditName?: string; creditUrl?: string };
        if (photo.creditName) {
          return { name: photo.creditName, url: photo.creditUrl };
        }
      }
    }
    return undefined;
  };

  // Get photo caption from the first photo if available
  const getPhotoCaption = (): string | undefined => {
    if (item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      const firstPhoto = item.photos[0];
      if (typeof firstPhoto === 'object' && firstPhoto && 'caption' in firstPhoto) {
        return (firstPhoto as { caption?: string }).caption;
      }
    }
    return undefined;
  };
  
  const photoUrl = getPhotoUrl();
  const photoCredit = getPhotoCredit();
  // Suppress the source-labeling captions ("From brand catalog", "From
  // community") that CopyToCollectionModal auto-stamps at copy time. The
  // corner "Community" pill badge (and the verified-brand equivalent)
  // already conveys source at a glance, so a bottom caption repeating it
  // is redundant visual noise. Actual user-authored captions (e.g., "in
  // sunlight", "second coat") still render normally.
  const SOURCE_LABEL_CAPTIONS = new Set(['From brand catalog', 'From community']);
  const rawCaption = getPhotoCaption();
  const photoCaption =
    rawCaption && SOURCE_LABEL_CAPTIONS.has(rawCaption) ? undefined : rawCaption;
  
  // Get stats
  const collectorCount = isBrandItem 
    ? (item as BrandItem).stats?.collectors || 0
    : (item as AggregatedItem).collectorCount || 0;
  
  const wishlistCount = isBrandItem
    ? (item as BrandItem).stats?.wishlisted || 0
    : (item as AggregatedItem).wishlistCount || 0;

  // Get status badge for official items
  const getStatusBadge = () => {
    if (!isBrandItem) return null;
    
    const brandItem = item as BrandItem;
    switch (brandItem.status) {
      case 'available':
        return (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
            Available
          </span>
        );
      case 'sold-out':
        return (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-white text-xs font-medium rounded-full">
            Sold Out
          </span>
        );
      case 'discontinued':
        return (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-gray-500 text-white text-xs font-medium rounded-full">
            Discontinued
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <Link
      to={polishHref}
      className={`block ${AppStyles.cardSubtle} overflow-hidden hover:shadow-md transition-shadow group`}
    >
      {/* Image */}
      <div className="aspect-square relative bg-gray-100">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <AppIcons.ViewAll className="w-12 h-12 text-gray-300" />
          </div>
        )}
        
        {/* Photo credit overlay - name only, no link (links in detail modals) */}
        {photoCredit?.name && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 pt-6">
            <span className="text-xs text-white/90">📷 {photoCredit.name}</span>
          </div>
        )}
        
        {/* Status badge */}
        {getStatusBadge()}
        
        {/* Source badge — Verified for official brand catalog items,
            Community for aggregated user data. Mutually exclusive; sits
            in the same corner so a tile always has exactly one badge
            regardless of which path built it. */}
        {isCommunityItem ? (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-blue-500/80 text-white text-xs font-medium rounded-full backdrop-blur-sm">
            Community
          </span>
        ) : (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-emerald-500/85 text-white text-xs font-medium rounded-full backdrop-blur-sm flex items-center gap-1">
            <span aria-hidden="true">✓</span>
            <span>Verified</span>
          </span>
        )}

        {/* Type badge — bottom-right, only for non-polish types so users
            can distinguish a plate from a polish at a glance within a
            mixed-type collection panel. Polish returns null and renders
            no badge (it's the default majority). */}
        <TypeBadge itemType={rawItem.itemType} />
        
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCollection();
              }}
              className="flex items-center gap-1 px-3 py-2 bg-white text-gray-800 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors shadow-lg"
            >
              <AppIcons.Add className="w-4 h-4" />
              Collection
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToWishlist();
              }}
              className="flex items-center gap-1 px-3 py-2 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 transition-colors shadow-lg"
            >
              <AppIcons.Wishlist className="w-4 h-4" />
              Wishlist
            </button>
          </div>
        </div>
      </div>

      {/* Photo caption */}
      {photoCaption && (
        <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
          <p className="text-xs text-gray-500 italic">{photoCaption}</p>
        </div>
      )}

      {/* Content */}
      <div className="p-3">
        {/* Name */}
        <h3 className="font-medium text-gray-900 truncate" title={item.name}>
          {item.name}
        </h3>
        
        {/* Color description */}
        {item.colorDescription && (
          <p className="text-sm text-gray-500 truncate mt-0.5" title={item.colorDescription}>
            {item.colorDescription}
          </p>
        )}
        
        {/* Collection name */}
        {item.collectionName && (
          <p className="text-xs text-purple-600 mt-1">
            {item.collectionName}
          </p>
        )}
        
        {/* Price (for official items) */}
        {isBrandItem && (item as BrandItem).price !== undefined && (
          <p className="text-sm font-medium text-gray-900 mt-2">
            ${(item as BrandItem).price?.toFixed(2)}
          </p>
        )}
        
        {/* Stats */}
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          {collectorCount > 0 && (
            <span className="flex items-center gap-1" title={`${collectorCount} collectors`}>
              <AppIcons.Collectors className="w-3 h-3" />
              {collectorCount}
            </span>
          )}
          {wishlistCount > 0 && (
            <span className="flex items-center gap-1" title={`${wishlistCount} wishlisted`}>
              <AppIcons.Wishlist className="w-3 h-3" />
              {wishlistCount}
            </span>
          )}
          {(confirmCount ?? 0) >= COMMUNITY_VERIFIED_MIN && (
            <span
              className="flex items-center gap-0.5 text-emerald-600 font-medium"
              title={`Confirmed by ${confirmCount} ${confirmCount === 1 ? 'collector' : 'collectors'}`}
            >
              ✓ {confirmCount}
            </span>
          )}
        </div>
        
        {/* Structured tag preview — combined chip strip covering finish,
            baseType, holoType, specialFeatures with distinct colors per
            category so viewers can tell a magnetic apart from a linear
            holo at a glance. Applies to BOTH BrandItem (verified brand
            products) and AggregatedItem (community items) — the
            AggregatedItem projection was extended in brandService to
            carry these fields. Non-polish item cards continue to
            render their own tags[] via their type-specific card
            components; this block only fires when the polish-side
            structured fields are populated. */}
        {(() => {
          const raw = item as unknown as {
            finish?: string[];
            specialFeatures?: string[];
          };
          type Chip = { key: string; label: string; className: string };
          const chips: Chip[] = [];
          // Cards are a browse element — the question users are asking
          // while scanning a grid is "which of these is magnetic /
          // thermal / holo?" So we render only Finish and Special
          // Features here; base type and holo type are detail-page
          // questions. Same canonicalize + title-case rules as the
          // detail page (utils/tagDisplay).
          const fn = canonicalizeTags(raw.finish, FINISH_OPTIONS);
          fn.forEach((v) => {
            chips.push({
              key: `finish:${v}`,
              label: titleCaseTag(v),
              className: 'bg-purple-100 text-purple-700',
            });
          });
          const sf = canonicalizeTags(
            raw.specialFeatures,
            SPECIAL_FEATURE_OPTIONS
          );
          sf.forEach((v) => {
            chips.push({
              key: `sf:${v}`,
              label: titleCaseTag(v),
              className: 'bg-sky-100 text-sky-700',
            });
          });
          if (chips.length === 0) return null;
          // Cap card real-estate at 4 visible chips + overflow indicator.
          const visible = chips.slice(0, 4);
          const overflow = chips.length - visible.length;
          return (
            <div className="flex flex-wrap gap-1 mt-2">
              {visible.map((chip) => (
                <span
                  key={chip.key}
                  className={`px-1.5 py-0.5 text-xs rounded ${chip.className}`}
                >
                  {chip.label}
                </span>
              ))}
              {overflow > 0 && (
                <span className="text-xs text-gray-400">+{overflow}</span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Mobile-friendly action buttons (always visible on mobile) */}
      <div className="flex border-t border-gray-100 md:hidden">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddToCollection();
          }}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-gray-700 hover:bg-gray-50 border-r border-gray-100"
        >
          <AppIcons.Add className="w-4 h-4" />
          Add
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddToWishlist();
          }}
          className="flex-1 flex items-center justify-center gap-1 py-2 text-sm text-amber-600 hover:bg-amber-50"
        >
          <AppIcons.Wishlist className="w-4 h-4" />
          Wishlist
        </button>
      </div>
    </Link>
  );
}
